/**
 * This enum represents each possible state that a WebData could be in.
 *
 * It should be private to this file/module. If you want to find out in
 * which state a WebData is, use one of the provided typeguards:
 *
 * - isWebDataSuccess()
 * - isWebDataFetching()
 * - isWebDataError()
 * - isWebDataNotAsked()
 */
enum WebDataState {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  FETCHING = 'FETCHING',
  NOT_ASKED = 'NOT_ASKED',
}

interface Success<T> {
  type: WebDataState.SUCCESS;
  data: T;
}

interface Error<T = {}> {
  type: WebDataState.ERROR;
  error: {
    message?: string;
    statusCode?: number;
    data?: T;
  };
}

interface Fetching {
  type: WebDataState.FETCHING;
}

interface NotAsked {
  type: WebDataState.NOT_ASKED;
}

export const webDataSuccess = <T>(successData: T): Success<T> => ({
  type: WebDataState.SUCCESS,
  data: successData,
});

export const webDataError = <T = {}>(message?: string, statusCode?: number, data?: T): Error<T> => ({
  type: WebDataState.ERROR,
  error: {
    message,
    statusCode,
    data,
  },
});

export const webDataFetching = (): Fetching => ({
  type: WebDataState.FETCHING,
});

export const webDataNotAsked = (): NotAsked => ({
  type: WebDataState.NOT_ASKED,
});

export type WebData<T> = Success<T> | Error | Fetching | NotAsked;

export const isWebDataSuccess = <T>(webData: WebData<T>): webData is Success<T> => {
  return webData.type === WebDataState.SUCCESS;
};

export const isWebDataFetching = <T>(webData: WebData<T>): webData is Fetching => {
  return webData.type === WebDataState.FETCHING;
};

export const isWebDataError = <T>(webData: WebData<T>): webData is Error => {
  return webData.type === WebDataState.ERROR;
};

export const isWebDataNotAsked = <T>(webData: WebData<T>): webData is NotAsked => {
  return webData.type === WebDataState.NOT_ASKED;
};

/**
 * Usage example:
 *
 * const numberWD = webDataSuccess<number>(5347);
 * const mapNumberToString = (n: number) => `${n}`;
 * const webDataTransformer = mapWebData(mapNumberToString) // could be used directly like `mapWebData(mapNumberToString)(numberWD);
 * const stringWD = webDataTransformer(numberWD);
 *
 *
 * Or on a selector:
 *
 * const selectStartEndIndexes = createSelector(
 *   selectPathGeojson,
 *   selectStartTimeInMillis,
 *   selectEndTimeInMilis,
 *   mapWebData(getStartEndIndexes)
 * );
 *
 * Note that types are inferred, so there's no need to make them explicit on calling the function;
 */

type WebDataOrData<Data> = WebData<Data> | Data;
type ArrayOfWebDataOrData<Tuple> = {
  [Key in keyof Tuple]: WebDataOrData<Tuple[Key]>;
};

export const mapWebData =
  <InputData extends any[], OutputData>(mapFn: (..._args: InputData) => OutputData) =>
  (...args: ArrayOfWebDataOrData<InputData>): WebData<OutputData> => {
    /**
     * `extracted` is created as an array of `undefined`s of length `args.length`
     * so technically this cast to `InputData` (that is a tuple representing each
     * argument type for `mapFn`) is incorrect. However after the for-loop it becomes
     * correct and giving the array this type, makes it possible to feed it to `mapFn`.
     *
     * Why was it made this way? (You can skip this)
     *
     * It could be `Partial<InputData>` making its type correct but it wouldn't
     * add safety since it'd be necessary to make a type-guard ("isInputData()");
     * yet that is tricky because we can't compare types in runtime (like
     * `typeof extracted === InputData`), neither can we check for `undefined`,
     * since one of the arguments could legitimately be `undefined`.
     *
     * Also, we could use a more direct approach like `map` but implementations in
     * Typescript of this function return an array of a single type. We weren't able
     * to parameterize the function given to `map` for each position of the args array.
     *
     * Another solution would be to have multiple definitions of the same function,
     * each with a varying number of arguments (as we had before).
     */
    const extracted: InputData = new Array(args.length) as InputData;

    /**
     * Those variables could be "True" all at the same time, read the end of the function
     * to understand how we handle those cases.
     */
    let error: Error | undefined;
    let fetching = false;
    let notAsked = false;

    /**
     * Inside this for-loop, `args[i]` has type `any` but `extracted`
     * has type `InputData`, so outside this function (before and after)
     * everything is strongly typed.
     */
    for (let i = 0; i < args.length; i += 1) {
      const wdtype = args[i]?.type;

      if (!(wdtype in WebDataState)) {
        /**
         * If the value isn't a WebData we simply pass it through, as we
         * can already feed it to `mapFn`, without any modifications.
         */
        extracted[i] = args[i];

        continue;
      }

      switch (wdtype) {
        case WebDataState.SUCCESS:
          /**
           * For every webdata that is a `success`, we extract the data
           * to be processed via `mapFn`.
           */
          extracted[i] = args[i].data;
          break;

        case WebDataState.ERROR:
          /**
           * If any webdata is an `error`, we capture its value to return it,
           * i.e. the whole "mapWebData" will return one error only.
           * Currently if we have more than one error, this variable is being
           * overwritten: capturing and returning the last one seen.
           *
           * TODO: group all errors and send a list of those.
           */
          error = args[i];
          break;

        case WebDataState.FETCHING:
          /**
           * If there is *no* `error` but some webdata is `fetching`, we return
           * that state.
           */
          fetching = true;
          break;

        case WebDataState.NOT_ASKED:
          /**
           * If there is *neither* an `error` nor a `fetching` but some webdata
           * is `not asked`, we return that state.
           */
          notAsked = true;
          break;
        default:
          break;
      }
    }

    /**
     * This sequence of `return` statements is highly opinionated:
     *
     * As said above, all those variables could be "True" at the same time.
     *
     * - If that's the case we return a single value in the `Error` state (currently
     *   capturing only one of the possible multiple different errors).
     * - If we have multiple webData arguments in both `Fetching` and `NotAsked` states
     *   but none in the `Error` state, we return `Fetching`.
     * - If there is no argument representing `Error` nor `Fetching`, but one or more in
     *   `NotAsked`, we return it.
     * - Finally, if there are only plain values or webDatas in `Success` state, we run
     *   `mapFn` on those extracted values and return a `Success`
     */
    if (error) {
      return error;
    }
    if (fetching) {
      return webDataFetching();
    }
    if (notAsked) {
      return webDataNotAsked();
    }

    return webDataSuccess<OutputData>(mapFn(...extracted));
  };
